import React from "react";

import { Button, Typography, Box } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import ModernModal from "../../../../../common/ModernModal";
import { projectsService } from "../../../../services";

interface Props {
  handleOpenDeleteDialog: () => void;
  openDeleteDialog: boolean;
  projectId: string;
  projectName: string;
}

export default function DeleteDialog({
  handleOpenDeleteDialog,
  openDeleteDialog,
  projectId,
  projectName,
}: Props) {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ["delete-project"],
    mutationFn: () => projectsService.deleteProject(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      handleOpenDeleteDialog();
    },
  });

  const handleConfirm = async () => {
    await mutateAsync();
  };

  return (
    <ModernModal
      open={openDeleteDialog}
      onClose={handleOpenDeleteDialog}
      title="Delete Project"
      maxWidth="sm"
      actions={
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={handleOpenDeleteDialog}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={isPending}
            sx={{
              background:
                "linear-gradient(135deg, #e53e3e, #c53030) !important",
              color: "#ffffff !important",
              fontWeight: 600,
              boxShadow: "0 4px 12px rgba(229, 62, 62, 0.3) !important",
              "&:hover": {
                background:
                  "linear-gradient(135deg, #c53030, #9c2626) !important",
                boxShadow: "0 6px 20px rgba(229, 62, 62, 0.4) !important",
                transform: "translateY(-1px)",
              },
              "&:disabled": {
                background:
                  "linear-gradient(135deg, #fc8181, #f56565) !important",
                color: "#ffffff !important",
                boxShadow: "none",
                transform: "none",
              },
              "&.MuiButton-contained": {
                background:
                  "linear-gradient(135deg, #e53e3e, #c53030) !important",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #c53030, #9c2626) !important",
                },
                "&:disabled": {
                  background:
                    "linear-gradient(135deg, #fc8181, #f56565) !important",
                },
              },
            }}
          >
            {isPending ? "Deleting..." : "Delete Project"}
          </Button>
        </Box>
      }
    >
      <Typography variant="body1" sx={{ mb: 2 }}>
        Are you sure you want to delete this project?
      </Typography>
      <Typography variant="body2" color="text.secondary">
        This action cannot be undone and the project{" "}
        <Typography component="span" fontWeight={600} color="text.primary">
          "{projectName}"
        </Typography>{" "}
        will be permanently removed along with all its episodes and data.
      </Typography>
    </ModernModal>
  );
}
